/**
 * CTA Boiler Plate module
 *
 * Not technically a module, it exists outside of the Modules loop.
 * If content is set on a Page only, this block will be rendered
 * right above the global boilerplate module if it exists, or above.
 * the footer.
 *
 */

 import get from "lodash.get";
 import { gutters, layout } from "~/styleguide";
 import { fluid } from "~/styleguide/utils";
 import { textStyles, themeText, themeHeadline } from "~/styleguide/typography";
 import { combineRules } from "fela";
 import { createComponent, FelaComponent } from "react-fela";

 import { useEffect, useRef } from "react";
 import globalData from "~/data/site-data.json";
 import { getThemeForModule } from "~/context/theme";

 import { ContentMaxWidth } from "~/components/layout/max-width";
 import { loadHubSpotFormsScript } from "~/components/shared/hubspot/form";

 export default function CTABoilerplate({ pageType }) {
   const globalFields = get(globalData, "data");

   const dataTarget = globalFields;

   const rawBoilerplateType = get(dataTarget, "acf.cta_boilerplate_post_types", []);
   const boilerplateTypes = Array.isArray(rawBoilerplateType) ? rawBoilerplateType : [rawBoilerplateType];
   const themeColors = get(dataTarget, "acf.cta_boilerplate_theme_colors");
   const image = get(dataTarget, "acf.cta_boilerplate_image");
   const imageUrl = typeof image === "string" ? image : get(image, "url");
   const heading = get(dataTarget, "acf.cta_boilerplate_heading");
   const copy = get(dataTarget, "acf.cta_boilerplate_copy");
   const form = get(dataTarget, "acf.cta_boilerplate_hubspot_form_id");

   const theme = getThemeForModule(themeColors);
   const textTheme = themeText(theme);
   const headlineTheme = themeHeadline(theme);

   if (!heading && !copy && !boilerplateTypes.length) return null;
   if (!boilerplateTypes.includes(pageType)) return null;

   const formContainerRef = useRef(null);

   useEffect(() => {
     if (!form || !formContainerRef.current) return;

     loadHubSpotFormsScript().then(() => {
       if (
         window.hbspt &&
         window.hbspt.forms &&
         typeof window.hbspt.forms.create === "function"
       ) {
         window.hbspt.forms.create({
           region: "na1",
           portalId: "5265893",
           formId: form,
           target: `#${formContainerRef.current.id}`
         });
       }
     });
   }, [form]);

   return (
     <Container
       extend={{
         background: theme.gradient || theme.primary,
         ...(imageUrl && {
           backgroundImage: `url(${imageUrl})`,
           backgroundSize: "cover",
           backgroundRepeat: "no-repeat",
           backgroundPosition: "center"
         })
       }}
       className="cta-boilerplate"
     >
       <ContentMaxWidth extend={colWrapStyles({ textSide: "left" })}>
         <Col textSide="left">
           <FelaComponent style={{ maxWidth: 600 }}>
             {heading && (
               <FelaComponent
                 style={combineRules(
                   ctaFormHeadingStyle(!!copy),
                   headlineTheme,
                   textStyles.heading2
                 )}
               >
                 <span dangerouslySetInnerHTML={{ __html: heading }} />
               </FelaComponent>
             )}
             {copy && (
               <CTAFormText
                 as="p"
                 extend={combineRules(textTheme, textStyles.paragraphLarge)}
                 dangerouslySetInnerHTML={{ __html: copy }}
                 passThrough={["dangerouslySetInnerHTML"]}
               />
             )}
           </FelaComponent>
         </Col>

         <Col textSide="left">
           <div
             ref={formContainerRef}
             id={`hubspot-boilerplate-${form}`}
             className="hs-form-frame"
             style={FormStyle}
           />
         </Col>
       </ContentMaxWidth>
     </Container>
   );
 }

 // ========== Styles ==========

 const Container = createComponent(
   ({ extend: e = {} }) => ({
     paddingTop: 87,
     paddingBottom: 87,
     extend: [fluid("paddingH", gutters.default), e],
     minWidthMobile: {
       extend: [fluid("paddingV", gutters.vertical)],
     },
   })
 );

 const colWrapStyles = ({ textSide }) => ({
   display: "flex",
   justifyContent: "space-between",
   alignItems: "stretch",
   maxWidth: 1300,
   position: "relative",
   maxWidthMobile: {
     flexWrap: "wrap",
   },
   extend: [
     {
       condition: textSide === "right",
       style: {
         flexDirection: "row-reverse",
       },
     },
   ],
 });

 const colMargin = (direction) =>
   fluid(direction, {
     min: 20,
     max: 40,
     maxWidth: layout.contentMaxWidth,
   });

 const Col = createComponent(({ textSide }) => ({
   position: "relative",
   display: "flex",
   alignItems: "center",
   alignContent: "center",
   flexWrap: "wrap",
   ":first-of-type": {
     width: "55%",
   },
   ":last-of-type": {
     width: "45%",
   },
   maxWidthMobile: {
     width: "100%!important",
     textAlign: "center",
     ":first-of-type": {
       marginBottom: 44,
     },
   },
   extend: [
     {
       condition: textSide === "left",
       style: {
         minWidthTablet: {
           ":first-of-type": {
             extend: [colMargin("marginRight")],
           },
         },
         minWidthMobile: {
           ":last-of-type": {
             extend: [colMargin("marginLeft")],
             alignItems: "stretch",
           },
         },
       },
     },
   ],
 }));

 const CTAFormText = createComponent(
   ({ subheading }) => ({
     extend: [
       {
         condition: subheading,
         style: {
           marginBottom: 30,
         },
       },
     ],
   }),
   "div",
   ["dangerouslySetInnerHTML"]
 );

 const FormStyle = {
   width: "100%",
 };

 const ctaFormHeadingStyle = (hasCopy) => ({
   extend: [hasCopy ? fluid("marginBottom", { min: 20, max: 50 }) : { marginBottom: 0 }],
 });
